/* eslint-disable no-empty */
import React, { useMemo, useState } from "react";
import axios from "axios";
import { IMaskInput } from "react-imask";

// Hosts that actually serve /.netlify/functions/api-proxy. Optimizers like
// NitroPack rehost the widget JS on the WordPress origin while keeping the
// original tools host inside the rewritten path, so match a known host
// anywhere in the src rather than trusting the URL's origin.
const TOOLS_HOSTS = ["tools.unclekam.com", "tools-unclekam.netlify.app"];

function toolsOriginFromSrc(src) {
  if (!src) return "";
  try {
    const u = new URL(src, location.href);
    if (TOOLS_HOSTS.includes(u.hostname)) return u.origin;
  } catch {}
  for (const host of TOOLS_HOSTS) {
    if (src.includes(`/${host}/`) || src.includes(`//${host}/`)) {
      return `https://${host}`;
    }
  }
  return "";
}

function detectToolsOrigin() {
  const fromCur = toolsOriginFromSrc(document.currentScript && document.currentScript.src);
  if (fromCur) return fromCur;
  const scripts = Array.from(document.getElementsByTagName("script"));
  for (let i = scripts.length - 1; i >= 0; i--) {
    const fromScan = toolsOriginFromSrc(scripts[i].src || "");
    if (fromScan) return fromScan;
  }
  // NOTE: do NOT fall back to location.href — when this form is embedded on
  // another site (e.g. the WordPress page at unclekam.com) that origin does
  // not serve /.netlify/functions/api-proxy and every request 404s. Fall back
  // to the tools host that actually hosts the proxy function.
  const isQA = new URLSearchParams(location.search).has("qa_mode");
  return isQA ? "https://tools-unclekam.netlify.app" : "https://tools.unclekam.com";
}

const FIELD_CONFIG = [
  { type: "text",   name: "firstname", label: "First Name *" },
  { type: "text",   name: "lastname",  label: "Last Name *" },
  { type: "email",  name: "email",     label: "Email Address *" },
  { type: "tel",    name: "phone",     label: "Phone Number *", mask: "(000) 000-0000" },
];

const PHONE_REGEX = /^\(\d{3}\) \d{3}-\d{4}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const StepHeader = React.memo(function StepHeader({ isDark, currentStep, totalSteps }) {
  const progressPct = Math.round((currentStep / totalSteps) * 100);
  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-2 pt-2 pb-2">
        <span className={isDark ? "text-light" : "text-dark"}>{`Step ${currentStep} of ${totalSteps}`}</span>
        <span className={isDark ? "text-light" : "text-dark"}>{`${progressPct}% Complete`}</span>
      </div>
      <div className="ukf-progress-track mb-3">
        <div className={`ukf-progress-bar ${isDark ? "dark" : ""}`} style={{ width: `${progressPct}%` }} />
      </div>
    </>
  );
});

const Step1 = React.memo(function Step1({ formData, setField, errors }) {
  return (
    <div className="row g-3 mt-4">
      {FIELD_CONFIG.map((cfg) => {
        const colClass = cfg.name === "firstname" || cfg.name === "lastname" ? "col-md-6" : "col-12";
        const val = formData[cfg.name] || "";
        const invalid = Boolean(errors[cfg.name]);
        const baseClass = `form-control ${invalid ? "is-invalid" : ""}`;

        return (
          <div className={colClass} key={cfg.name}>
            {cfg.mask ? (
              <>
                <IMaskInput
                  id={cfg.name}
                  name={cfg.name}
                  className={baseClass}
                  placeholder={cfg.label}
                  mask={cfg.mask}
                  lazy={true}
                  overwrite={true}
                  defaultValue={val}
                  onAccept={(v) => { if (v !== formData[cfg.name]) setField(cfg.name, v); }}
                  type={cfg.type}
                  inputMode="tel"
                  autoComplete="off"
                />
                {invalid && <div className="invalid-feedback">{errors[cfg.name] === 'User with this email already exists.' ? '' : errors[cfg.name]}</div>}
              </>
            ) : (
              <>
                <input
                  id={cfg.name}
                  name={cfg.name}
                  className={baseClass}
                  placeholder={cfg.label}
                  type={cfg.type}
                  value={val}
                  onChange={(e) => setField(cfg.name, e.target.value)}
                  autoComplete="off"
                />
                {invalid && <div className="invalid-feedback">{errors[cfg.name] === 'User with this email already exists.' ? '' : errors[cfg.name]}</div>}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
});

export default function GhlLeadForm({
  theme = "light",
  qa_mode,
  redirectUrl = "https://app.unclekam.com/dashboard",
  keyword = "unclekam",
  proxyUrl,
}) {
  const css = `
    .ukf-progress-track{height:6px;background:#e9ecef;border-radius:999px}
    .ukf-progress-bar{height:6px;border-radius:999px;background:#4f58ff;transition:width .25s}
    .ukf-progress-bar.dark{background:#5a6bff}
    .ukf-success-check{font-size:2.75rem;line-height:1}
    .ukf-pointer{cursor:pointer}
    .ukf-muted-link{text-decoration:underline}
  `;

  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const qa = useMemo(
    () => (typeof qa_mode === "boolean" ? qa_mode : params.has("qa_mode")),
    [qa_mode, params]
  );

  const PROXY_URL =
    proxyUrl ||
    window.__UK_PROXY_URL__ ||
    `${detectToolsOrigin()}/.netlify/functions/api-proxy`;

  const isDark = theme === "dark";

  const [formData, setFormData] = useState(() => ({
    firstname: params.get("firstname") || "",
    lastname:  params.get("lastname")  || "",
    email:     params.get("email")     || "",
    phone:     params.get("phone")     || "",
    zip:       params.get("zip")       || "",
    options:   [],
    income:    "",
    agreementAccepted: false,
  }));

  const [errors, setErrors] = useState({}); // { firstname?, lastname?, email?, phone?, _global? }
  const [submitting, setSubmitting] = useState(false);

  const setField = (name, value) =>
    setFormData((s) => (s[name] === value ? s : { ...s, [name]: value }));

  const validateStep1 = () => {
    const e = {};
    if (!String(formData.firstname).trim()) e.firstname = "First name is required.";
    if (!String(formData.lastname).trim())  e.lastname  = "Last name is required.";
    if (!String(formData.email).trim())     e.email     = "Email is required.";
    else if (!EMAIL_REGEX.test(formData.email)) e.email = "Enter a valid email address.";
    if (!String(formData.phone).trim())     e.phone     = "Phone number is required.";
    else if (!PHONE_REGEX.test(formData.phone)) e.phone = "Use format (123) 456-7890.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // Try to map a variety of API error shapes to field errors
  const mapServerErrors = (err) => {
    const e = {};
    const data = err?.response?.data ?? {};
    // Common shapes: {errors:{field:"msg"}}, {errors:[{path:"field",message:"msg"}]}, {message:"..."}
    if (data.errors && typeof data.errors === "object" && !Array.isArray(data.errors)) {
      for (const [k, v] of Object.entries(data.errors)) e[k] = String(v);
    } else if (Array.isArray(data.errors)) {
      data.errors.forEach((it) => {
        const key = it?.path || it?.field || it?.param;
        const msg = it?.message || it?.msg || it?.error || "Invalid value.";
        if (key) e[key] = msg;
      });
    } else if (data.message) {
      // Heuristics to attach to a field if we can guess
      const msg = String(data.message);
      if (/email/i.test(msg)) e.email = msg;
      else if (/phone/i.test(msg)) e.phone = msg;
      else e._global = msg;
    } else {
      e._global = "Server error. Please try again.";
    }
    setErrors((prev) => ({ ...prev, ...e }));
  };

  const proId = params.get("pro_id");
  const proIdParam = proId ? `&pro_id=${encodeURIComponent(proId)}` : "";

  const handleSubmit = async () => {
    if (!validateStep1()) return;

    setSubmitting(true);
    setErrors({}); // clear before request
    try {
      await axios.post(
        PROXY_URL,
        {
          path: "/create-user-ghl",
          method: "POST",
          qa_mode: qa,
          data: {
            firstname: formData.firstname,
            lastname:  formData.lastname,
            email:     formData.email,
            phone:     formData.phone,
          },
        },
        { headers: { "Content-Type": "application/json" } }
      );

      window.location.href = `${redirectUrl}?email=${encodeURIComponent(formData.email)}&keyword=${encodeURIComponent(keyword)}${proIdParam}${qa ? "&qa_mode=on" : ""}`;
    } catch (err) {
      if(err?.response?.data?.message === "User with this email already exists.") {
      window.location.href = `${redirectUrl}?email=${encodeURIComponent(formData.email)}&keyword=${encodeURIComponent(keyword)}${proIdParam}${qa ? "&qa_mode=on" : ""}`;
      }
      mapServerErrors(err);
      setSubmitting(false);
    }
  };

  const SumbmitButton = () => {
    const disabled = submitting;
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={handleSubmit}
        className="btn btn-success px-4"
      >
        {submitting ? (
          <>
            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
            Submitting…
          </>
        ) : "Submit"}
      </button>
    );
  };

  return (
    <div className={`container px-3 px-md-4 py-3 ${isDark ? "" : ""}`}>
      <style>{css}</style>
      <div className="mx-auto" style={{ maxWidth: 860 }}>
        <form onSubmit={(e) => e.preventDefault()}>
          <div className="mb-3 mt-4">
            <Step1 isDark={isDark} formData={formData} setField={setField} errors={errors} />
          </div>

          <div className="d-flex justify-content-between align-items-center mt-3">
            <SumbmitButton />
          </div>
        </form>
      </div>
    </div>
  );
}
